"use strict";

/**
 * @classdesc
 * Implementierung des Feature-Service zur Abfrage von Objekten.
 *
 * @class
 * @extends gc.gis.service.AbstractFeatureService
 * @memberof gc.gis.service
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.util.GCRequestManager} requestManager Objekt zur Ausführung von Requests
 * @param {string} identifyUrl Service-URL zum Identifizieren von Objekten
 * @param {string} identifyDetailUrl Service-URL zum Abfruf von Detail-Informationen zu einem Objekt
 * @param {string} queryMapFeatureUrl Service-URL zum Abfruf von Features (Vektordaten) zur einer Kartenansicht
 */
gc.gis.service.QueryFeatureService = function(requestManager, identifyUrl, identifyDetailUrl, queryMapFeatureUrl)
{
	gc.gis.service.AbstractFeatureService.call(this, requestManager);

	/**
	 * @type {string}
	 */
	this._identifyUrl       = identifyUrl;

	/**
	 * @type {string}
	 */
	this._identifyDetailUrl = identifyDetailUrl;

	/**
	 * @type {string}
	 */
	this._queryMapFeatureUrl = queryMapFeatureUrl;
};
gc.gis.service.QueryFeatureService.prototype = Object.create(gc.gis.service.AbstractFeatureService.prototype);

/**
 * Führt eine Identifizieren-Abfrage durch.
 *
 * @param {gc.gis.layer.GCLayer} layer der abzufragende Layer
 * @param {gc.gis.geom.GCGeometry} geometry Abfragegeometrie
 * @param {gc.gis.map.GCMapExtent} map_extent Kartenausdehnung
 * @param {gc.gis.map.GCMapSize} map_size Kartengröße
 * @param {Object} opt_options optionale Aufrufoptionen
 * @param {int} [opt_options.limit] max. Anzahl der zurückzugebenden Features
 */
gc.gis.service.QueryFeatureService.prototype.identify = function(layer, geometry, map_extent, map_size, opt_options)
{
	var self = this;
	opt_options = opt_options || {};

	if (opt_options.limit !== undefined && opt_options.limit < 1)
	{
		return;
	}

	var format = gc.gis.iface.mapFactory.createFormat();
	var params = {
		layers: layer.getName(),
		geometry: format.writeGeometry(geometry, gc.gis.format.WKT),
		geometryFormat: gc.gis.format.WKT,
		crs: geometry.getCRS(),
		mapExtent: map_extent.join(','),
		mapSize: map_size.join(',')
	};

	if (opt_options.limit)
	{
		params.limit = opt_options.limit;
	}

	// Request abfeuern
	this._requestManager.ajax({
		label: 'Lade Objekt-Informationen ... ',
		method: 'POST',
		context: this,
		url: this._identifyUrl,
		data: params,
		dataType: gc.gis.util.GCRequestManagerDataType.JSON,
		success: function(data)
		{
			self._fireEvent(gc.gis.service.FeatureServiceEvent.IDENTIFY_SUCCESS, {
				layer: layer,
				features: self._readLayerResult(data)
			});
		},
		error: function(err)
		{
			self._fireEvent(gc.gis.service.FeatureServiceEvent.IDENTIFY_ERROR, {
				layer: layer,
				error: err
			});
		}
	});
};

/**
 * Führt eine Detail-Abfrage für ein Objekt durch.
 *
 * @param {gc.gis.layer.GCLayer} layer der abzufragende Layer
 * @param {string} fid ID des abzufragenden Features
 * @param {string} feature_layer Layer welcher die Daten enthält (bei Gruppenlayer außer FachDB-Layer)
 */
gc.gis.service.QueryFeatureService.prototype.identifyDetail = function(layer, fid, feature_layer)
{
	var self = this;
	var params = {
		layer:          layer.getName(),
		featureId:      fid,
		featureLayer:   feature_layer
	};

	// Detail-Request abfeuern
	gc.gis.iface.request.ajax({
		context: this,
		url: self._identifyDetailUrl,
		data: params,
		dataType: gc.gis.util.GCRequestManagerDataType.JSON,
		success: function(data)
		{
			self._fireEvent(gc.gis.service.FeatureServiceEvent.IDENTIFY_DETAIL_SUCCESS, {
				layer: layer,
				featureId: fid,
				featureLayer: feature_layer,
				detailResult: data
			});
		},
		error: function(err)
		{
			self._fireEvent(gc.gis.service.FeatureServiceEvent.IDENTIFY_DETAIL_ERROR, {
				layer: layer,
				error: err
			});
		}
	});
};

/**
 * Führt eine Abfrage von Features (Vektordaten) zur einer Kartenansicht durch.
 *
 * @param {gc.gis.layer.GCLayer} layer der abzufragende Layer
 * @param {gc.gis.geom.GCGeometry} geometry Abfragegeometrie
 * @param {gc.gis.map.GCMapExtent} map_extent Kartenausdehnung
 * @param {gc.gis.map.GCMapSize} map_size Kartengröße
 * @param {Object} opt_options optionale Aufrufoptionen
 * @param {int} [opt_options.limit] max. Anzahl der zurückzugebenden Features
 * @param {int} [opt_options.offset] Startindex für Paginierung
 * @param {boolean} [opt_options.returnGeometry] Flag, ob die Geometrien zurückgegeben werden sollen (Default: true)
 * @param {boolean} [opt_options.returnIdsOnly] Flag, ob nur die IDs zurückgegeben werden sollen (Default: false)
 * @param {boolean} [opt_options.returnWritableOnly] Flag, ob nur Objekte mit Schreibzugriff zurückgegeben werden sollen
 *      (Default: false)
 * @param {boolean} [opt_options.ignoreSupportFeatureList] Flag, ob die Layer-Einstellung
 *      "Layer bei 'GetFeatureList' berücksichtigen" ignoriert werden soll. Dies ist z.B. bei der Bearbeitunsgfunktion
 *      sinnvoll (Default: false)
 * @param {boolean} [opt_options.message] Nutzer-Meldung die während der Abfrage angezeigt wird
 */
gc.gis.service.QueryFeatureService.prototype.queryMapFeatures = function(layer, geometry, map_extent, map_size,
                                                                         opt_options)
{
	var self = this;
	opt_options = opt_options || {};

	if (opt_options.limit !== undefined && opt_options.limit < 1)
	{
		return;
	}

	var format = gc.gis.iface.mapFactory.createFormat();
	var params = {
		layers: layer.getName(),
		geometry: format.writeGeometry(geometry, gc.gis.format.WKT),
		geometryFormat: gc.gis.format.WKT,
		crs: geometry.getCRS(),
		mapExtent: map_extent.join(','),
		mapSize: map_size.join(',')
	};

	if (opt_options.limit)
	{
		params.limit = opt_options.limit;
	}
	if (opt_options.offset !== undefined  && opt_options.offset > 0)
	{
		params.offset = opt_options.offset;
	}
	if (opt_options.returnGeometry !== undefined)
	{
		params.returnGeometry = opt_options.returnGeometry;
	}
	if (opt_options.returnIdsOnly !== undefined)
	{
		params.returnIdsOnly = opt_options.returnIdsOnly;
	}
	if (opt_options.returnWritableOnly !== undefined)
	{
		params.returnWritableOnly = opt_options.returnWritableOnly;
	}
	if (opt_options.ignoreSupportFeatureList !== undefined)
	{
		params.ignoreSupportFeatureList = opt_options.ignoreSupportFeatureList;
	}

	// Request abfeuern
	this._requestManager.ajax({
		label: opt_options.message || 'Lade Objekte zur Kartenansicht ... ',
		context: this,
		url: this._queryMapFeatureUrl,
		data: params,
		dataType: gc.gis.util.GCRequestManagerDataType.JSON,
		success: function(data)
		{
			self._fireEvent(gc.gis.service.FeatureServiceEvent.QUERY_MAP_FEATURES_SUCCESS, {
				layer: layer,
				features: self._readLayerResult(data)
			});
		},
		error: function(err)
		{
			self._fireEvent(gc.gis.service.FeatureServiceEvent.QUERY_MAP_FEATURES_ERROR, {
				layer: layer,
				error: err
			});
		}
	});
};

/**
 * Erzeugt aus einer Menge von Result-Objekten eine Liste von Feature-Instanzen zu weiteren Verwendung.
 *
 * @param {Object} layer_data
 * @return {gc.gis.feature.GCFeature[]}
 * @private
 */
gc.gis.service.QueryFeatureService.prototype._readLayerResult = function(layer_data)
{
	var features = [],
		i, ii;

	if (layer_data.features && layer_data.features.length)
	{
		for (i = 0, ii = layer_data.features.length; i < ii; i++)
		{
			features.push(this._readFeature(layer_data.layer, layer_data.layerCRS, layer_data.features[i]));
		}
	}

	return features;
};

/**
 * Erzeugt aus einem Result-Objekt eine Feature-Instanz zu weiteren Verwendung.
 *
 * @param {string} layer_name
 * @param {string} layer_crs
 * @param {Object} feature_data
 * @return {gc.gis.service.feature.AbstractQueryFeature}
 * @private
 */
gc.gis.service.QueryFeatureService.prototype._readFeature = function(layer_name, layer_crs, feature_data)
{
	/**
	 * @var gc.gis.service.feature.AbstractQueryFeature
	 */
	var feature;
	/**
	 * @var gc.gis.format.GCFormat;
	 */
	var format = gc.gis.iface.mapFactory.createFormat();
	var geom_format, geom_crs;
	var i, ii;


	if (feature_data.type)
	{
		switch (feature_data.type.toLowerCase())
		{
			case gc.gis.service.FeatureServiceTypes.FEATURETYPE_FEATURE.toLowerCase():
				feature = new gc.gis.service.feature.QuerySimpleFeature();
				break;
			case gc.gis.service.FeatureServiceTypes.FEATURETYPE_FACHDB.toLowerCase():
				feature = new gc.gis.service.feature.QueryFachDBFeature();
				break;
			default:
				throw new gc.gis.error.GCError('Unbekannte Objektart "' + feature_data.type + '".');
		}
	}

	if (feature_data.geometryFormat)
	{
		switch (feature_data.geometryFormat.toLowerCase())
		{
			case gc.gis.service.FeatureServiceTypes.GEOM_FORMAT_WKT:
				geom_format = gc.gis.format.WKT;
				break;
			case gc.gis.service.FeatureServiceTypes.GEOM_FORMAT_GEOJSON:
				geom_format = gc.gis.format.GEOJSON;
				break;
			case gc.gis.service.FeatureServiceTypes.GEOM_FORMAT_NONE:
				break;
			default:
				throw new gc.gis.error.GCError('Fehler beim Parsen der Objekte. Unbekanntes Geometrieformat "'
					+ feature_data.geometryFormat + '".');

		}
	}

	geom_crs = feature_data.geometryCRS;

	feature.setID(feature_data.featureId);
	feature.setFeatureLayer(feature_data.featureLayer);
	//feature.setAttribute('layer', layer_name);
	//feature.setAttribute('layerCRS', layer_crs);

	if (feature_data.properties)
	{
		feature.setAttributes(feature_data.properties);
	}
	if (feature_data.titleProperty)
	{
		feature.setTitleName(feature_data.titleProperty);
	}
	if (feature_data.actions)
	{
		feature.setActions(feature_data.actions);
	}
	if (feature_data.formattedFeatureInfo)
	{
		feature.setHTMLDetailResult(feature_data.formattedFeatureInfo);
	}

	if (feature instanceof gc.gis.service.feature.QuerySimpleFeature)
	{
		if (feature_data.geometry && geom_crs)
		{
			feature.setGeometry(format.readGeometry(feature_data.geometry, geom_format, geom_crs));
		}
	}
	else if (feature instanceof gc.gis.service.feature.QueryFachDBFeature)
	{
		if (Array.isArray(feature_data.fb_koordinaten))
		{
			for (i = 0, ii = feature_data.fb_koordinaten.length; i < ii; i++)
			{
				if (feature_data.fb_koordinaten[i].geometry && geom_crs)
				{
					feature_data.fb_koordinaten[i].geometry =
						format.readGeometry(feature_data.fb_koordinaten[i].geometry, geom_format, geom_crs)
				}
			}

			feature.setGeometryList(feature_data.fb_koordinaten);
		}

		if (Array.isArray(feature_data.fb_adressen))
		{
			feature.setAddressList(feature_data.fb_adressen);
		}

		if (Array.isArray(feature_data.fb_flurstuecke))
		{
			feature.setLandparcelList(feature_data.fb_flurstuecke);
		}

		if (Array.isArray(feature_data.fb_media))
		{
			feature.setMediaList(feature_data.fb_media);
		}
	}

	return feature;
};